'use client'
import React, { useState } from 'react'
import styles from './ProfileCompleteness.module.scss'
import SectionCard from '../components/SectionCard'
import Icon, { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import FixedColorIcon, {
  FixedColorIcons,
} from '@crystal-eyes/components/elements/Icon/FixedColorIcon/FixedColorIcon'
import ProfileComplete from '../components/ProfileComplete'
import ProfileIncompleteCTA from '../components/ProfileIncompleteCTA'
import usePersonality from '@crystal-eyes/hooks/profiles/usePersonality'
import useAssessments from '@crystal-eyes/hooks/profiles/personality/useAssessments'
import usePeerReview from '@crystal-eyes/hooks/peer_review/usePeerReview'
import { useParams, useRouter } from 'next/navigation'
import LoadingBar from '@crystal-eyes/components/elements/Loader/LoadingBar'

interface Identity {
  id: string
}

export default function ProfileCompleteness({
  identity,
}: {
  identity: Identity
}) {
  const [isCopied, setIsCopied] = useState(false)
  const router = useRouter()
  const params = useParams()
  const { data: personality, loading: personalityLoading } = usePersonality({
    identity,
  })
  const { data: assessments, loading: assessmentsLoading } = useAssessments({
    identity,
  })
  const { data: peerReviewsData, loading: peerReviewsLoading } = usePeerReview({
    identity,
  })
  const predictionComplete = !!personality?.prediction?.discType
  const assessmentComplete = !!assessments?.disc?.takenAt
  const peerReviewCount =
    peerReviewsData?.reviewsFromPeers?.peerReviews?.length || 0
  const completenessCount =
    (predictionComplete ? 1 : 0) +
    (assessmentComplete ? 1 : 0) +
    peerReviewCount

  const completenessPercentage = (completenessCount / 5) * 100

  if (completenessCount > 4) return null

  const copyURL = () => {
    if (!window) return
    const url = window.location.href
    navigator.clipboard.writeText(url)

    setIsCopied(true)

    setTimeout(() => {
      setIsCopied(false)
    }, 1500)
  }

  const goToLinkedInPrediciton = () => {
    if (params)
      router.replace(`/p/${identity.id.replace('snap:', '')}?prediction=true`)
  }

  const isLoading =
    personalityLoading || assessmentsLoading || peerReviewsLoading
  return (
    <SectionCard
      cardId="profile_completeness"
      icon={Icons.CheckMarkOutOfCircle}
      title="Profile Completeness"
      className={styles.profileCompleteness}
    >
      {isLoading ? (
        <LoadingCompleteness />
      ) : (
        <div className={styles.profileCompletenessContainer}>
          <div className={styles.status}>
            <div className={styles.statusHeader}>INCOMPLETE</div>
            <div className={styles.statusParentBar}>
              <div
                className={styles.statusInnerBar}
                style={{ width: `${completenessPercentage}%` }}
              >
                {completenessCount > 0 ? `${completenessCount} of 5` : ''}
              </div>
            </div>
          </div>
          {predictionComplete ? (
            <ProfileComplete
              title="Prediction signals"
              icon={FixedColorIcons.Browser}
            />
          ) : (
            <ProfileIncompleteCTA
              title="Prediction signals"
              subtext="See what your public data says about your personality and strengthen your profile by entering your LinkedIn URL"
              CTA="RUN PREDICTION"
              action={goToLinkedInPrediciton}
            />
          )}
          {assessmentComplete ? (
            <ProfileComplete
              title="DISC assessment"
              icon={FixedColorIcons.CheckmarkInBox}
            />
          ) : (
            <ProfileIncompleteCTA
              title="DISC assessment"
              subtext="Strengthen your prediction by answering questions about your behavior"
              CTA="TAKE ASSESSMENT"
              action={() => router.push('/app/assessment')}
            />
          )}
          <div
            className={`${styles.peerReviewContainer} ${styles.completenessBox}`}
          >
            <div className={styles.prChartWrapper}>
              <div className={styles.peerReviewCompletenessChart}>
                <div
                  className={`${
                    peerReviewCount > 2
                      ? styles.completeLine
                      : styles.incompleteLine
                  } ${styles.statusLine}`}
                />
                <div
                  className={`${
                    peerReviewCount > 1
                      ? styles.completeLine
                      : styles.incompleteLine
                  } ${styles.statusLine}`}
                />
                <div
                  className={`${
                    peerReviewCount > 0
                      ? styles.completeLine
                      : styles.incompleteLine
                  } ${styles.statusLine}`}
                />
              </div>
              <div
                className={styles.grayHeader}
                style={{ marginTop: '2px' }}
              >{`Peer reviews (${peerReviewCount})`}</div>
            </div>
            <div className={styles.prDetails}>
              <div className="CE subtext">
                Profiles are strengthened when peers review your personality.
                Share your profile link and request a peer review
              </div>
              <div onClick={copyURL} className={styles.copyLinkTxt}>
                {isCopied ? (
                  <FixedColorIcon
                    icon={FixedColorIcons.CheckmarkGreenCircle}
                    className={styles.greenCheckMark}
                  />
                ) : (
                  <Icon icon={Icons.Copy} className={styles.copied} />
                )}
                {isCopied ? 'COPIED!' : 'COPY PROFILE LINK'}
              </div>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  )
}

function LoadingCompleteness() {
  return (
    <div className={styles.loadingArea}>
      <LoadingBar width={'100%'} />
      <LoadingBar width={'70%'} />
      <LoadingBar width={'100%'} />
      <LoadingBar width={'100%'} />
      <LoadingBar width={'80%'} />
      <LoadingBar width={'75%'} />
      <LoadingBar width={'40%'} />
      <LoadingBar width={'80%'} />
      <LoadingBar width={'70%'} />
      <LoadingBar width={'100%'} />
      <LoadingBar width={'100%'} />
    </div>
  )
}
